import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  Tooltip,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add,
  Delete,
  Storage,
  Business,
  Description,
  Close
} from '@mui/icons-material';
import { fetchDatabases, createDatabase, deleteDatabase, fetchClients } from '../api/admin';
import { Database, DatabaseCreateInput, Client } from '../types/admin';
import { useUser } from './UserContext';

const THEME = {
  BG_PRIMARY: "#000000",
  BG_SECONDARY: "#1a1a1a",
  BG_TERTIARY: "#2d2d2d",
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  TEXT_MUTED: "#808080",
  ACCENT_PRIMARY: "#2196f3",
  ACCENT_SUCCESS: "#4caf50",
  ACCENT_ERROR: "#ff4444",
  ACCENT_INFO: "#00bcd4",
  BORDER_SUBTLE: "#333333",
  BORDER_DEFAULT: "#555555",
};

interface DatabasesManagerProps {
  onClose?: () => void;
}

export const DatabasesManager: React.FC<DatabasesManagerProps> = ({ onClose }) => {
  const { user } = useUser();
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'superadmin';
  
  const [databases, setDatabases] = useState<Database[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDb, setSelectedDb] = useState<Database | null>(null);
  
  const [formData, setFormData] = useState<DatabaseCreateInput>({
    name: '',
    db_host: '',
    db_port: 3306,
    db_user: '',
    db_password: '',
    client_id: 0,
    description: ''
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Загружаем только клиентов (для фильтрации и создания БД)
      const clientsData = await fetchClients();
      
      // Фильтруем клиентов для Admin (только своей организации)
      let filteredClients = clientsData;
      if (!isSuperAdmin && user?.organization_id) {
        filteredClients = clientsData.filter(
          (client) => client.organization_id === user.organization_id
        );
      }
      
      setClients(filteredClients);
      
      // Пытаемся загрузить базы данных
      try {
        const dbData = await fetchDatabases();
        
        // Фильтруем базы данных для Admin (только БД клиентов своей организации)
        let filteredDatabases = dbData;
        if (!isSuperAdmin && user?.organization_id) {
          const clientIds = filteredClients.map(c => c.id);
          filteredDatabases = dbData.filter(
            (db) => clientIds.includes(db.client_id)
          );
        }
        
        setDatabases(filteredDatabases);
        setError(null);
      } catch (dbErr: any) {
        // Если ошибка 403 - у админа нет доступа к базам данных
        if (dbErr.response?.status === 403) {
          setError('⚠️ Accesso alle basi di dati negato. Solo Super Admin può gestire i database.');
          setDatabases([]);
        } else {
          throw dbErr; // Прокидываем другие ошибки выше
        }
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Accesso negato. Contatta il Super Admin.');
      } else {
        setError(err.response?.data?.detail || 'Impossibile caricare i dati');
      }
      // Error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user?.organization_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getClientName = (clientId: number) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'N/A';
  };

  const handleCreateDatabase = async () => {
    if (!formData.name.trim()) {
      setFormError('Il nome è obbligatorio');
      return;
    }
    if (!formData.db_host.trim()) {
      setFormError('L\'host è obbligatorio');
      return;
    }
    if (!formData.db_user.trim()) {
      setFormError('L\'utente è obbligatorio');
      return;
    }
    if (!formData.db_password.trim()) {
      setFormError('La password è obbligatoria');
      return;
    }
    if (!formData.client_id) {
      setFormError('Seleziona un cliente');
      return;
    }

    try {
      setSubmitting(true);
      setFormError(null);
      await createDatabase(formData);
      setCreateDialogOpen(false);
      setFormData({ 
        name: '', 
        db_host: '', 
        db_port: 3306, 
        db_user: '', 
        db_password: '', 
        client_id: 0, 
        description: '' 
      });
      await loadData();
      // После загрузки данных обновляем localStorage DBS
      setTimeout(() => {
        localStorage.setItem('userDBS', JSON.stringify(databases));
      }, 500);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Errore nella creazione');
      // Error('Error creating database:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDatabase = async () => {
    if (!selectedDb) return;

    try {
      setSubmitting(true);
      await deleteDatabase(selectedDb.id);
      setDeleteDialogOpen(false);
      setSelectedDb(null);
      await loadData();
    } catch (err: any) {
      let errorMessage = 'Errore nell\'eliminazione';
      
      if (err.response?.status === 403) {
        errorMessage = '❌ Accesso negato. Non hai i permessi per eliminare questo database.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
      // Error('Error deleting database:', err);
      setDeleteDialogOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: THEME.ACCENT_PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
            🗄️ Gestione Database
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
            {isSuperAdmin ? 'Gestisci i database di tutti i clienti' : 'Gestisci i database dei tuoi clienti'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isSuperAdmin && (
            <Tooltip title="Crea nuovo database">
              <IconButton
                onClick={() => setCreateDialogOpen(true)}
                sx={{
                  bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.15),
                  color: THEME.ACCENT_SUCCESS,
                  '&:hover': {
                    bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.25),
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                <Add />
              </IconButton>
            </Tooltip>
          )}
          
          {onClose && (
            <Tooltip title="Chiudi">
              <IconButton 
                onClick={onClose}
                sx={{ 
                  color: THEME.TEXT_SECONDARY,
                  '&:hover': { 
                    bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
                    color: THEME.ACCENT_ERROR
                  }
                }}
              >
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {databases.map((db) => (
          <Grid item xs={12} md={6} lg={4} key={db.id}>
            <Fade in timeout={300}>
              <Card
                sx={{
                  bgcolor: THEME.BG_SECONDARY,
                  border: `1px solid ${THEME.BORDER_SUBTLE}`,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: THEME.ACCENT_INFO,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 16px ${alpha(THEME.ACCENT_INFO, 0.2)}`
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Storage sx={{ color: THEME.ACCENT_INFO, fontSize: 32 }} />
                    {isSuperAdmin && (
                      <Tooltip title="Elimina database">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedDb(db);
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            color: THEME.ACCENT_ERROR,
                            '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.1) }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600, mb: 1 }}>
                    {db.name}
                  </Typography>

                  {db.description && !db.description.includes('Imported from allowed_databases') && (
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                      <Description sx={{ fontSize: 16, color: THEME.TEXT_SECONDARY, mt: 0.3 }} />
                      <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                        {db.description}
                      </Typography>
                    </Box>
                  )}

                  <Chip
                    icon={<Business />}
                    label={db.client_name || getClientName(db.client_id)}
                    size="small"
                    sx={{
                      bgcolor: alpha('#ff9800', 0.2),
                      color: '#ff9800',
                      fontWeight: 600,
                      border: `1px solid ${alpha('#ff9800', 0.3)}`,
                      mt: 1
                    }}
                  />

                  <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY, display: 'block', mt: 2 }}>
                    Creato: {new Date(db.created_at).toLocaleDateString('it-IT')}
                  </Typography>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {databases.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Storage sx={{ fontSize: 64, color: THEME.TEXT_SECONDARY, mb: 2 }} />
          <Typography variant="h6" sx={{ color: THEME.TEXT_SECONDARY }}>
            Nessun database trovato
          </Typography>
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Nuovo Database
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Nome Database"
            fullWidth
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{
              mb: 2,
              mt: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Host"
            fullWidth
            placeholder="localhost"
            value={formData.db_host}
            onChange={(e) => setFormData({ ...formData, db_host: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Porta"
            fullWidth
            type="number"
            value={formData.db_port}
            onChange={(e) => setFormData({ ...formData, db_port: parseInt(e.target.value) || 3306 })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Utente"
            fullWidth
            value={formData.db_user}
            onChange={(e) => setFormData({ ...formData, db_user: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Password"
            fullWidth
            type="password"
            value={formData.db_password}
            onChange={(e) => setFormData({ ...formData, db_password: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
            required
          />
          <TextField
            label="Descrizione"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
          />
          <FormControl
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: THEME.SURFACE_GLASS,
                color: THEME.TEXT_PRIMARY,
                '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
              },
              '& .MuiInputLabel-root': {
                color: THEME.TEXT_MUTED,
                '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
              }
            }}
          >
            <InputLabel>Cliente</InputLabel>
            <Select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: Number(e.target.value) })}
              label="Cliente"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: THEME.BG_SECONDARY,
                    border: `1px solid ${THEME.BORDER_SUBTLE}`,
                    '& .MuiMenuItem-root': {
                      color: THEME.TEXT_PRIMARY,
                      '&:hover': {
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1)
                      },
                      '&.Mui-selected': {
                        bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.15),
                        '&:hover': {
                          bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2)
                        }
                      }
                    }
                  }
                }
              }}
            >
              <MenuItem value={0} disabled>Seleziona un cliente</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setFormData({ 
                name: '', 
                db_host: '', 
                db_port: 3306, 
                db_user: '', 
                db_password: '', 
                client_id: 0, 
                description: '' 
              });
              setFormError(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleCreateDatabase}
            variant="contained"
            disabled={submitting}
            sx={{
              bgcolor: THEME.ACCENT_PRIMARY,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.8) }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Conferma Eliminazione
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
            Sei sicuro di voler eliminare il database <strong>{selectedDb?.name}</strong>?
            Questa azione è irreversibile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedDb(null);
            }}
            disabled={submitting}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteDatabase}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
